import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Alert,
  Grid
} from '@mui/material';

import { useEstimateDetail } from '../../../hooks/useEstimateDetail';
import { useEstimateActions } from '../../../hooks/useEstimateActions';
import EstimateDetailSkeleton from '../../../components/estimates/EstimateDetailSkeleton';
import EstimateHeader from '../../../components/estimates/EstimateHeader';
import EstimateContent from '../../../components/estimates/EstimateContent';
import EstimateDialogs from '../../../components/estimates/EstimateDialogs';
import EstimatePDFPreview from '../../../components/estimates/EstimatePDFPreview';
import DocumentAuditTrail from '../../../components/audit/DocumentAuditTrail';

const EstimateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  
  // Custom hooks for data and actions
  const { estimate, loading, error, refreshEstimate } = useEstimateDetail(id);
  const estimateActions = useEstimateActions(estimate);

  // Loading state
  if (loading) {
    return <EstimateDetailSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Not found state
  if (!estimate) {
    return (
      <Box>
        <Alert severity="error">Estimate not found</Alert>
      </Box>
    );
  }

  // Handle successful actions that need to refresh data
  const handleActionSuccess = async (result: { success: boolean; updatedEstimate?: any }) => {
    if (result.success && result.updatedEstimate) {
      // Update the estimate data if provided
      // This would be handled by the custom hook
      await refreshEstimate();
    }
  };

  return (
    <Box>
      <EstimateHeader
        estimate={estimate}
        permissions={estimateActions.permissions}
        actionLoading={estimateActions.actionLoading}
        onEdit={estimateActions.handleEditEstimate}
        onSend={() => estimateActions.openDialog('sendDialogOpen')}
        onDownloadPDF={estimateActions.handleDownloadPDF}
        onConvert={() => estimateActions.openDialog('convertDialogOpen')}
        onDelete={() => estimateActions.openDialog('deleteDialogOpen')}
        onAccept={estimateActions.handleAcceptEstimate}
        onReject={estimateActions.handleRejectEstimate}
        onExpire={estimateActions.handleExpireEstimate}
      />

      <Grid container spacing={3}>
        {/* Estimate Details */}
        <Grid item xs={12} lg={8}>
          <EstimateContent estimate={estimate} />
          
          {/* Audit Trail */}
          <Box mt={3}>
            <DocumentAuditTrail
              documentId={estimate.id}
              documentType="estimate"
              defaultExpanded={false}
            />
          </Box>
        </Grid>

        {/* PDF Preview */}
        <Grid item xs={12} lg={4}>
          <EstimatePDFPreview 
            estimate={estimate}
            onDownloadPDF={estimateActions.handleDownloadPDF}
          />
        </Grid>
      </Grid>

      <EstimateDialogs
        estimate={estimate}
        dialogs={estimateActions.dialogs}
        actionLoading={estimateActions.actionLoading}
        defaultSendData={estimateActions.defaultSendData}
        defaultConvertData={estimateActions.defaultConvertData}
        onCloseDialog={estimateActions.closeDialog}
        onSendEstimate={async (data) => {
          const result = await estimateActions.handleSendEstimate(data);
          await handleActionSuccess(result);
          return result;
        }}
        onConvertToSalesOrder={estimateActions.handleConvertToSalesOrder}
        onDeleteEstimate={estimateActions.handleDeleteEstimate}
      />
    </Box>
  );
};

export default EstimateDetail;